import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import './index.scss';

import { CustomButton } from '../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { useBeforeUnloadWarning, useScreenWidth } from '../../../../shared/Helpers/hooks';
import Behaviours from './Behaviors';
import Targets from './targets';
import UnsavedTargetsWarning from './UnsavedTargetsWarning';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { clearSessionInfo, endSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { clearErrors, createSoapNote } from '../../../../redux/State/clientSlice/soapNoteSlice';
import { setInfoEvent } from '../../../../redux/State/clientSlice/calendarHelperSlice';
import { TargetSessionStatuses } from '../../../../redux/API/ClientAPIHelpers/sessionsProperties';
import { isElcFunder } from '../../../../redux/API/constants';

const DataCollection = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { clientId, sessionId } = useParams();
	const data = useSelector((state) => state.session.sessionInfo);
	const targets = useSelector((state) => state.session.targets);
	const isNavMenuOpen = useSelector((state) => state.common.isMenuOpen);
	const isSavingGoals = useSelector((state) => Boolean(sessionId && state.dataCollection.pendingGoalSaves[sessionId]));

	const [showWarning, setShowWarning] = useState(false);
	const [pendingNavigation, setPendingNavigation] = useState<(() => void) | null>(null);

	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const hasUnsavedTargets = targets.some((target) => target.targetStatus === TargetSessionStatuses.open);

	useBeforeUnloadWarning(hasUnsavedTargets);

	const navigateEditSoapNote = ({ reportId }: { clientId: string; reportId: string; sessionId: string }) => {
		dispatch(setInfoEvent(false));
		dispatch(clearSessionInfo());
		dispatch(clearErrors());

		navigate(`/soap-notes/info?clientId=${clientId}&reportId=${reportId}&sessionId=${sessionId}`);
	};

	const navigateToSoapNoteHandler = () => {
		if (!clientId || !sessionId) return;

		const reportId = data?.reportId;

		const performNavigation = () => {
			if (!reportId) {
				dispatch(createSoapNote({ clientId, sessionId }))
					.then(unwrapResult)
					.then(({ reportId: newReportId }) => {
						if (!newReportId) return;
						navigateEditSoapNote({ clientId, reportId: newReportId, sessionId });
					})
					.catch(errorNormalizer);

				return;
			}
			navigateEditSoapNote({ clientId, reportId, sessionId });
		};

		if (hasUnsavedTargets) {
			setPendingNavigation(() => performNavigation);
			setShowWarning(true);
		} else {
			performNavigation();
		}
	};

	const handleConfirm = () => {
		setShowWarning(false);

		if (pendingNavigation) {
			pendingNavigation();
			setPendingNavigation(null);
		}
	};

	const handleCancel = () => {
		setShowWarning(false);
		setPendingNavigation(null);
	};

	// data.id is '' in the initial Redux state and becomes the real session UUID once getSessionInfo
	// resolves (dispatched by Targets on mount). Guard on this so ELC sessions never briefly render
	// "Start SOAP Note" during the async gap before the real requiresSoapNote value arrives.
	const isSessionLoaded = Boolean(data?.id);
	// Data-collection-only session types (e.g. ELC) complete without a SOAP note and return to the
	// calendar. Clinical types (insurance, DOE, etc.) still require a SOAP note.
	const requiresSoapNote = data?.sessionType?.requiresSoapNote ?? true;
	const isElcSession = isElcFunder(data?.sessionType?.funderId);

	const saveDraftHandler = () => {
		if (isSavingGoals) return;

		dispatch(setInfoEvent(false));
		dispatch(clearSessionInfo());
		navigate('/calendar');
	};

	const closeSessionWithoutNoteHandler = () => {
		if (!sessionId) return;

		const performClose = () => {
			dispatch(endSession(sessionId))
				.then(unwrapResult)
				.then(() => {
					dispatch(setInfoEvent(false));
					dispatch(clearSessionInfo());
					navigate('/calendar');
				})
				.catch(errorNormalizer);
		};

		if (hasUnsavedTargets) {
			setPendingNavigation(() => performClose);
			setShowWarning(true);
		} else {
			performClose();
		}
	};

	return (
		<>
			<Targets />
			<div
				className="targetsFooter"
				style={{ width: isMobile ? '100%' : isNavMenuOpen ? 'calc(100% - 372px)' : 'calc(100% - 84px)' }}
			>
				<Behaviours />
				<div className="action">
					{isSessionLoaded && isElcSession && !data?.isCompleted && (
						<CustomButton
							title="Save Draft"
							buttonType="secondaryGray"
							onClick={saveDraftHandler}
							disabled={isSavingGoals}
						/>
					)}
					{!isSessionLoaded ? (
						<CustomButton title="" loading />
					) : requiresSoapNote ? (
						<CustomButton title="Start SOAP Note" onClick={navigateToSoapNoteHandler} />
					) : data?.isCompleted ? (
						<CustomButton title="Session Ended" disabled />
					) : (
						<CustomButton title="Close Session" onClick={closeSessionWithoutNoteHandler} />
					)}
				</div>
			</div>
			<UnsavedTargetsWarning open={showWarning} onCancel={handleCancel} onConfirm={handleConfirm} />
		</>
	);
};

export default DataCollection;
